import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Plus, Edit, Trash2, Search, Camera, Globe, Check, ChevronsUpDown, Calendar, DollarSign, UserCircle, Upload, Download, X, FileText, Monitor, MapPin, Users, UserCog, Shield, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLanguage } from '@/i18n/LanguageContext';
import StaffFormWizard from '@/components/admin/StaffFormWizard';
import SalarySetupWizard from '@/components/admin/SalarySetupWizard';

// Days of the week for working days selection
const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday', short: 'Mon' },
  { value: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { value: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { value: 'thursday', label: 'Thursday', short: 'Thu' },
  { value: 'friday', label: 'Friday', short: 'Fri' },
  { value: 'saturday', label: 'Saturday', short: 'Sat' },
  { value: 'sunday', label: 'Sunday', short: 'Sun' },
];

// Multi-select Working Days Component
function WorkingDaysSelect({ value = [], onValueChange, testId }) {
  const [open, setOpen] = useState(false);
  
  const toggleDay = (day) => {
    if (value.includes(day)) {
      onValueChange(value.filter(d => d !== day));
    } else {
      onValueChange([...value, day]);
    }
  };
  
  const selectedLabels = DAYS_OF_WEEK
    .filter(d => value.includes(d.value))
    .map(d => d.short)
    .join(', ');
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <div className="flex items-center gap-2 truncate">
            <Calendar className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="truncate">
              {selectedLabels || "Select working days..."}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-2" align="start">
        <div className="space-y-1">
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day.value}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors",
                value.includes(day.value) 
                  ? "bg-indigo-100 text-indigo-900" 
                  : "hover:bg-gray-100"
              )}
              onClick={() => toggleDay(day.value)}
            >
              <div className={cn(
                "w-5 h-5 rounded border flex items-center justify-center",
                value.includes(day.value) 
                  ? "bg-indigo-600 border-indigo-600" 
                  : "border-gray-300"
              )}>
                {value.includes(day.value) && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="font-medium">{day.label}</span>
            </div>
          ))}
        </div>
        <div className="border-t mt-2 pt-2 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onValueChange(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
          >
            Weekdays
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onValueChange(DAYS_OF_WEEK.map(d => d.value))}
          >
            All Days
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Login devices options
const LOGIN_DEVICES = [
  { value: 'desktop', label: 'Desktop', icon: '🖥️' },
  { value: 'mobile', label: 'Mobile', icon: '📱' },
  { value: 'web', label: 'Web', icon: '🌐' },
];

// Multi-select Login Devices Component
function LoginDevicesSelect({ value = [], onValueChange, testId }) {
  const [open, setOpen] = useState(false);
  
  const toggleDevice = (device) => {
    if (value.includes(device)) {
      // Don't allow deselecting if it's the last one
      if (value.length === 1) return;
      onValueChange(value.filter(d => d !== device));
    } else {
      onValueChange([...value, device]);
    }
  };
  
  const selectedLabels = LOGIN_DEVICES
    .filter(d => value.includes(d.value))
    .map(d => d.label)
    .join(', ');
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <div className="flex items-center gap-2 truncate">
            <Monitor className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="truncate">
              {selectedLabels || "Select devices..."}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-2" align="start">
        <div className="space-y-1">
          {LOGIN_DEVICES.map((device) => (
            <div
              key={device.value}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors",
                value.includes(device.value) 
                  ? "bg-indigo-100 text-indigo-900" 
                  : "hover:bg-gray-100"
              )}
              onClick={() => toggleDevice(device.value)}
            >
              <div className={cn(
                "h-4 w-4 flex items-center justify-center rounded border",
                value.includes(device.value) 
                  ? "bg-indigo-600 border-indigo-600 text-white" 
                  : "border-gray-300"
              )}>
                {value.includes(device.value) && (
                  <Check className="h-3 w-3" />
                )}
              </div>
              <span className="mr-2">{device.icon}</span>
              <span className="font-medium">{device.label}</span>
            </div>
          ))}
        </div>
        <div className="border-t mt-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onValueChange(['desktop', 'mobile', 'web'])}
          >
            Select All
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Common timezone list organized by region
const TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'UTC' },
  
  // Americas
  { value: 'America/New_York', label: 'America/New York (EST/EDT)', region: 'Americas' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)', region: 'Americas' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (PST/PDT)', region: 'Americas' },
  { value: 'America/Anchorage', label: 'America/Anchorage (AKST/AKDT)', region: 'Americas' },
  { value: 'America/Toronto', label: 'America/Toronto (EST/EDT)', region: 'Americas' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)', region: 'Americas' },
  { value: 'America/Mexico_City', label: 'America/Mexico City (CST/CDT)', region: 'Americas' },
  { value: 'America/Sao_Paulo', label: 'America/São Paulo (BRT)', region: 'Americas' },
  { value: 'America/Buenos_Aires', label: 'America/Buenos Aires (ART)', region: 'Americas' },
  { value: 'America/Bogota', label: 'America/Bogotá (COT)', region: 'Americas' },
  { value: 'America/Lima', label: 'America/Lima (PET)', region: 'Americas' },
  { value: 'America/Santiago', label: 'America/Santiago (CLT)', region: 'Americas' },
  
  // Europe
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Europe/Vienna (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Europe/Istanbul (TRT)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Europe/Athens (EET/EEST)', region: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET/EEST)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (CET/CEST)', region: 'Europe' },
  
  // Asia
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)', region: 'Asia' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (AST)', region: 'Asia' },
  { value: 'Asia/Tehran', label: 'Asia/Tehran (IRST)', region: 'Asia' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi (PKT)', region: 'Asia' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)', region: 'Asia' },
  { value: 'Asia/Mumbai', label: 'Asia/Mumbai (IST)', region: 'Asia' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (BST)', region: 'Asia' },
  { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT)', region: 'Asia' },
  { value: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)', region: 'Asia' },
  { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala Lumpur (MYT)', region: 'Asia' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong Kong (HKT)', region: 'Asia' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)', region: 'Asia' },
  { value: 'Asia/Taipei', label: 'Asia/Taipei (CST)', region: 'Asia' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)', region: 'Asia' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)', region: 'Asia' },
  { value: 'Asia/Manila', label: 'Asia/Manila (PHT)', region: 'Asia' },
  
  // Oceania
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)', region: 'Oceania' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)', region: 'Oceania' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)', region: 'Oceania' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)', region: 'Oceania' },
  { value: 'Australia/Adelaide', label: 'Australia/Adelaide (ACST/ACDT)', region: 'Oceania' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)', region: 'Oceania' },
  { value: 'Pacific/Fiji', label: 'Pacific/Fiji (FJT)', region: 'Oceania' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (HST)', region: 'Oceania' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Africa/Cairo (EET)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT)', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT)', region: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Africa/Casablanca (WET)', region: 'Africa' },
];

// Comprehensive Currency List with symbols and timezone mapping
const CURRENCIES = [
  // Major Currencies
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿' },
  
  // Asian Currencies
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: 'Rs', flag: '🇵🇰' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', flag: '🇧🇩' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', flag: '🇱🇰' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: 'Rs', flag: '🇳🇵' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', flag: '🇹🇭' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', flag: '🇮🇩' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: '🇵🇭' },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', flag: '🇻🇳' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷' },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$', flag: '🇹🇼' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰' },
  
  // Middle East Currencies
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', flag: '🇸🇦' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', flag: '🇶🇦' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', flag: '🇰🇼' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: '.د.ب', flag: '🇧🇭' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', flag: '🇴🇲' },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'د.ا', flag: '🇯🇴' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', flag: '🇮🇱' },
  { code: 'IRR', name: 'Iranian Rial', symbol: '﷼', flag: '🇮🇷' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷' },
  
  // European Currencies (non-Euro)
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', flag: '🇷🇺' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', flag: '🇭🇺' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', flag: '🇷🇴' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', flag: '🇧🇬' },
  { code: 'HRK', name: 'Croatian Kuna', symbol: 'kn', flag: '🇭🇷' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', flag: '🇺🇦' },
  
  // African Currencies
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', flag: '🇳🇬' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', flag: '🇪🇬' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', flag: '🇰🇪' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', flag: '🇬🇭' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'د.م.', flag: '🇲🇦' },
  
  // Latin American Currencies
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'Mex$', flag: '🇲🇽' },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'AR$', flag: '🇦🇷' },
  { code: 'CLP', name: 'Chilean Peso', symbol: 'CLP$', flag: '🇨🇱' },
  { code: 'COP', name: 'Colombian Peso', symbol: 'COL$', flag: '🇨🇴' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', flag: '🇵🇪' },
];

// Timezone to Currency mapping
const TIMEZONE_TO_CURRENCY = {
  // Americas
  'America/New_York': 'USD',
  'America/Chicago': 'USD',
  'America/Denver': 'USD',
  'America/Los_Angeles': 'USD',
  'America/Anchorage': 'USD',
  'America/Toronto': 'CAD',
  'America/Vancouver': 'CAD',
  'America/Mexico_City': 'MXN',
  'America/Sao_Paulo': 'BRL',
  'America/Buenos_Aires': 'ARS',
  'America/Bogota': 'COP',
  'America/Lima': 'PEN',
  'America/Santiago': 'CLP',
  
  // Europe
  'Europe/London': 'GBP',
  'Europe/Paris': 'EUR',
  'Europe/Berlin': 'EUR',
  'Europe/Rome': 'EUR',
  'Europe/Madrid': 'EUR',
  'Europe/Amsterdam': 'EUR',
  'Europe/Brussels': 'EUR',
  'Europe/Vienna': 'EUR',
  'Europe/Warsaw': 'PLN',
  'Europe/Moscow': 'RUB',
  'Europe/Istanbul': 'TRY',
  'Europe/Athens': 'EUR',
  'Europe/Helsinki': 'EUR',
  'Europe/Stockholm': 'SEK',
  'Europe/Zurich': 'CHF',
  
  // Asia
  'Asia/Dubai': 'AED',
  'Asia/Riyadh': 'SAR',
  'Asia/Tehran': 'IRR',
  'Asia/Karachi': 'PKR',
  'Asia/Kolkata': 'INR',
  'Asia/Mumbai': 'INR',
  'Asia/Dhaka': 'BDT',
  'Asia/Bangkok': 'THB',
  'Asia/Jakarta': 'IDR',
  'Asia/Singapore': 'SGD',
  'Asia/Kuala_Lumpur': 'MYR',
  'Asia/Hong_Kong': 'HKD',
  'Asia/Shanghai': 'CNY',
  'Asia/Taipei': 'TWD',
  'Asia/Seoul': 'KRW',
  'Asia/Tokyo': 'JPY',
  'Asia/Manila': 'PHP',
  
  // Oceania
  'Australia/Sydney': 'AUD',
  'Australia/Melbourne': 'AUD',
  'Australia/Brisbane': 'AUD',
  'Australia/Perth': 'AUD',
  'Australia/Adelaide': 'AUD',
  'Pacific/Auckland': 'NZD',
  'Pacific/Fiji': 'FJD',
  'Pacific/Honolulu': 'USD',
  
  // Africa
  'Africa/Cairo': 'EGP',
  'Africa/Johannesburg': 'ZAR',
  'Africa/Lagos': 'NGN',
  'Africa/Nairobi': 'KES',
  'Africa/Casablanca': 'MAD',
  
  // Default
  'UTC': 'USD',
};

// Get currency info by code
const getCurrencyInfo = (code) => {
  return CURRENCIES.find(c => c.code === code) || { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸' };
};

// Get currency for timezone
const getCurrencyForTimezone = (timezone) => {
  return TIMEZONE_TO_CURRENCY[timezone] || 'USD';
};

// Searchable Timezone Combobox Component
function TimezoneCombobox({ value, onValueChange, testId }) {
  const [open, setOpen] = useState(false);
  
  const selectedTimezone = TIMEZONES.find(tz => tz.value === value);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <div className="flex items-center gap-2 truncate">
            <Globe className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="truncate">
              {selectedTimezone ? selectedTimezone.label : "Select timezone..."}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone..." />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup heading="UTC">
              {TIMEZONES.filter(tz => tz.region === 'UTC').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Americas">
              {TIMEZONES.filter(tz => tz.region === 'Americas').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Europe">
              {TIMEZONES.filter(tz => tz.region === 'Europe').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Asia">
              {TIMEZONES.filter(tz => tz.region === 'Asia').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Oceania">
              {TIMEZONES.filter(tz => tz.region === 'Oceania').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup heading="Africa">
              {TIMEZONES.filter(tz => tz.region === 'Africa').map((tz) => (
                <CommandItem
                  key={tz.value}
                  value={tz.label}
                  onSelect={() => {
                    onValueChange(tz.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === tz.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {tz.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function StaffPage() {
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffFormSaving, setStaffFormSaving] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [usagePolicies, setUsagePolicies] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  
  // Salary modal states
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [salaryStaff, setSalaryStaff] = useState(null);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [salaryData, setSalaryData] = useState({
    salary_type: 'monthly',
    base_salary: 0,
    currency: 'USD',
    overtime_pay_enabled: false,
    overtime_rate: 1.5,
    overtime_calculation: 'after_shift',
    overtime_threshold_hours: 8,
    max_overtime_hours: 4,
    compliance_notes: '',
    late_penalty: { enabled: false, penalty_type: 'fixed', amount: 0 },
    absence_deduction: { rule: 'per_day', custom_amount: 0 },
    bonus_allowances: { fixed_monthly: 0, performance_bonus: 0, attendance_bonus: 0, travel_food_allowance: 0 },
    tax_settings: { enabled: false, country: '', percentage: 0 },
    payment_cycle: 'monthly'
  });
  
  // Personal Data modal states
  const [personalDataDialogOpen, setPersonalDataDialogOpen] = useState(false);
  const [personalDataStaff, setPersonalDataStaff] = useState(null);
  const [personalDataLoading, setPersonalDataLoading] = useState(false);
  const [nextOfKin, setNextOfKin] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // GPS Coordinates — navigate to dedicated page

  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    department: '',
    shift_start: '09:00',
    shift_end: '17:00',
    timezone: 'UTC',
    grace_period: 15,
    force_break_time: 0,
    allow_screenshot: false,
    screenshot_interval: 5,
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    allowed_login_devices: ['desktop', 'mobile', 'web'],
    status: 'active',
    staff_type: 'staff',
    team_ids: [],
    group_policy_id: '',
    telegram_alert: false
  });
  const [formError, setFormError] = useState('');
  const [teams, setTeams] = useState([]);
  const [telegramEnabled, setTelegramEnabled] = useState(false);

  useEffect(() => {
    fetchStaff();
    fetchUsagePolicies();
    fetchTeams();
    fetchCompanyInfo();
    fetchTelegramEnabled();
  }, []);
  
  const fetchTelegramEnabled = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/telegram`, { withCredentials: true });
      setTelegramEnabled(res.data?.enabled === true);
    } catch { /* ignore */ }
  };

  // Get default currency based on company timezone
  const getDefaultCurrency = () => {    if (companyInfo?.timezone) {
      return getCurrencyForTimezone(companyInfo.timezone);
    }
    return 'USD';
  };
  
  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/company`, {
        withCredentials: true
      });
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };
  
  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, {
        withCredentials: true
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUsagePolicies = async () => {
    try {
      const response = await axios.get(`${API}/admin/usage-policies`, {
        withCredentials: true
      });
      setUsagePolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch usage policies:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/admin/teams`, {
        withCredentials: true
      });
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };
  
  // Salary functions
  const openSalaryDialog = async (staffMember) => {
    setSalaryStaff(staffMember);
    setSalaryLoading(true);
    setSalaryDialogOpen(true);
    
    // Get default currency based on company timezone
    const defaultCurrency = getDefaultCurrency();
    
    try {
      const response = await axios.get(`${API}/admin/staff/${staffMember.staff_id}/salary`, {
        withCredentials: true
      });
      
      if (response.data.has_salary && response.data.salary) {
        setSalaryData({
          salary_type: response.data.salary.salary_type || 'monthly',
          base_salary: response.data.salary.base_salary || 0,
          currency: response.data.salary.currency || defaultCurrency,
          overtime_pay_enabled: response.data.salary.overtime_pay_enabled || false,
          overtime_rate: response.data.salary.overtime_rate ?? 1.5,
          overtime_calculation: response.data.salary.overtime_calculation || 'after_shift',
          overtime_threshold_hours: response.data.salary.overtime_threshold_hours || 8,
          max_overtime_hours: response.data.salary.max_overtime_hours ?? 4,
          compliance_notes: response.data.salary.compliance_notes || '',
          late_penalty: response.data.salary.late_penalty || { enabled: false, penalty_type: 'fixed', amount: 0 },
          absence_deduction: response.data.salary.absence_deduction || { rule: 'per_day', custom_amount: 0 },
          bonus_allowances: response.data.salary.bonus_allowances || { fixed_monthly: 0, performance_bonus: 0, attendance_bonus: 0, travel_food_allowance: 0 },
          tax_settings: response.data.salary.tax_settings || { enabled: false, country: '', percentage: 0 },
          payment_cycle: response.data.salary.payment_cycle || 'monthly'
        });
      } else {
        // Reset to defaults with company currency
        setSalaryData({
          salary_type: 'monthly',
          base_salary: 0,
          currency: defaultCurrency,
          overtime_pay_enabled: false,
          overtime_rate: 1.5,
          overtime_calculation: 'after_shift',
          overtime_threshold_hours: 8,
          max_overtime_hours: 4,
          compliance_notes: '',
          late_penalty: { enabled: false, penalty_type: 'fixed', amount: 0 },
          absence_deduction: { rule: 'per_day', custom_amount: 0 },
          bonus_allowances: { fixed_monthly: 0, performance_bonus: 0, attendance_bonus: 0, travel_food_allowance: 0 },
          tax_settings: { enabled: false, country: '', percentage: 0 },
          payment_cycle: 'monthly'
        });
      }
    } catch (error) {
      console.error('Failed to fetch salary:', error);
      toast.error('Failed to load salary data');
    } finally {
      setSalaryLoading(false);
    }
  };
  
  const handleSaveSalary = async () => {
    if (!salaryStaff) return;

    const payload = {
      ...salaryData,
      base_salary: Number(salaryData.base_salary) || 0,
      overtime_rate: Number(salaryData.overtime_rate) || 1.5,
      overtime_threshold_hours: Number(salaryData.overtime_threshold_hours) || 8,
      max_overtime_hours: Math.min(12, Math.max(0, Number(salaryData.max_overtime_hours) || 0)),
      late_penalty: {
        enabled: !!salaryData.late_penalty?.enabled,
        penalty_type: salaryData.late_penalty?.penalty_type || 'fixed',
        amount: Number(salaryData.late_penalty?.amount) || 0,
      },
      absence_deduction: {
        rule: salaryData.absence_deduction?.rule || 'per_day',
        custom_amount: Number(salaryData.absence_deduction?.custom_amount) || 0,
      },
      bonus_allowances: {
        fixed_monthly: Number(salaryData.bonus_allowances?.fixed_monthly) || 0,
        performance_bonus: Number(salaryData.bonus_allowances?.performance_bonus) || 0,
        attendance_bonus: Number(salaryData.bonus_allowances?.attendance_bonus) || 0,
        travel_food_allowance: Number(salaryData.bonus_allowances?.travel_food_allowance) || 0,
      },
      tax_settings: {
        enabled: !!salaryData.tax_settings?.enabled,
        country: salaryData.tax_settings?.country || '',
        percentage: Number(salaryData.tax_settings?.percentage) || 0,
      },
    };
    
    try {
      await axios.post(`${API}/admin/staff/${salaryStaff.staff_id}/salary`, payload, {
        withCredentials: true
      });
      
      toast.success('Salary setup saved successfully');
      setSalaryDialogOpen(false);
      
      // Update local staff data to reflect salary setup
      setStaff(prev => prev.map(s => 
        s.staff_id === salaryStaff.staff_id 
          ? { ...s, has_salary_setup: true }
          : s
      ));
    } catch (error) {
      console.error('Failed to save salary:', error);
      toast.error(error.response?.data?.detail || 'Failed to save salary');
    }
  };
  
  const handleDeleteSalary = async () => {
    if (!salaryStaff) return;
    
    try {
      await axios.delete(`${API}/admin/staff/${salaryStaff.staff_id}/salary`, {
        withCredentials: true
      });
      
      toast.success('Salary setup deleted');
      setSalaryDialogOpen(false);
      
      // Update local staff data
      setStaff(prev => prev.map(s => 
        s.staff_id === salaryStaff.staff_id 
          ? { ...s, has_salary_setup: false }
          : s
      ));
    } catch (error) {
      console.error('Failed to delete salary:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete salary');
    }
  };
  
  // Personal Data functions
  const openPersonalDataDialog = async (staffMember) => {
    setPersonalDataStaff(staffMember);
    setPersonalDataLoading(true);
    setPersonalDataDialogOpen(true);
    
    try {
      const response = await axios.get(
        `${API}/admin/staff/${staffMember.staff_id}/personal-data`,
        { withCredentials: true }
      );
      setNextOfKin(response.data.next_of_kin || []);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch personal data:', error);
      setNextOfKin([]);
      setDocuments([]);
    } finally {
      setPersonalDataLoading(false);
    }
  };
  
  const handleAddNextOfKin = () => {
    setNextOfKin([...nextOfKin, { name: '', relation: '', contact_number: '' }]);
  };
  
  const handleRemoveNextOfKin = (index) => {
    setNextOfKin(nextOfKin.filter((_, i) => i !== index));
  };
  
  const handleNextOfKinChange = (index, field, value) => {
    const updated = [...nextOfKin];
    updated[index][field] = value;
    setNextOfKin(updated);
  };
  
  const handleSavePersonalData = async () => {
    if (!personalDataStaff) return;
    
    try {
      await axios.put(
        `${API}/admin/staff/${personalDataStaff.staff_id}/personal-data`,
        { next_of_kin: nextOfKin },
        { withCredentials: true }
      );
      
      toast.success('Personal data saved successfully');
      
      // Update local staff data
      const hasData = nextOfKin.length > 0 || documents.length > 0;
      setStaff(prev => prev.map(s => 
        s.staff_id === personalDataStaff.staff_id 
          ? { ...s, has_personal_data: hasData }
          : s
      ));
    } catch (error) {
      console.error('Failed to save personal data:', error);
      toast.error(error.response?.data?.detail || 'Failed to save personal data');
    }
  };
  
  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !personalDataStaff) return;
    
    setUploadingDoc(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'personal');
      
      const response = await axios.post(
        `${API}/admin/staff/${personalDataStaff.staff_id}/documents`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      // Refresh documents
      const dataResponse = await axios.get(
        `${API}/admin/staff/${personalDataStaff.staff_id}/personal-data`,
        { withCredentials: true }
      );
      setDocuments(dataResponse.data.documents || []);
      
      // Update local staff data
      setStaff(prev => prev.map(s => 
        s.staff_id === personalDataStaff.staff_id 
          ? { ...s, has_personal_data: true }
          : s
      ));
      
      toast.success('Document uploaded successfully');
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };
  
  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/admin/staff/${personalDataStaff.staff_id}/documents/${doc.document_id}`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document');
    }
  };
  
  const handleDeleteDocument = async (docId) => {
    if (!personalDataStaff) return;
    
    try {
      await axios.delete(
        `${API}/admin/staff/${personalDataStaff.staff_id}/documents/${docId}`,
        { withCredentials: true }
      );
      
      setDocuments(documents.filter(d => d.document_id !== docId));
      
      // Update has_personal_data flag
      const hasData = nextOfKin.length > 0 || documents.length > 1;
      setStaff(prev => prev.map(s => 
        s.staff_id === personalDataStaff.staff_id 
          ? { ...s, has_personal_data: hasData }
          : s
      ));
      
      toast.success('Document deleted');
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };
  
  const handleCreateStaff = async (e) => {
    e.preventDefault();
    if (staffFormSaving) return;
    setFormError('');
    setStaffFormSaving(true);

    try {
      await axios.post(
        `${API}/admin/staff`,
        formData,
        { withCredentials: true }
      );
      
      // Reset form — seed timezone from company (not silent UTC)
      setFormData({
        name: '',
        email: '',
        password: '',
        department: '',
        shift_start: '09:00',
        shift_end: '17:00',
        timezone: companyInfo?.timezone || 'UTC',
        grace_period: 15,
        force_break_time: 0,
        allow_screenshot: false,
        screenshot_interval: 5,
        working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        allowed_login_devices: ['desktop', 'mobile', 'web'],
        status: 'active',
        staff_type: 'staff',
        team_ids: [],
        group_policy_id: ''
      });
      setCreateDialogOpen(false);
      fetchStaff();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        // Handle insufficient balance error
        if (errorDetail.message === 'Insufficient balance') {
          setFormError(`Insufficient balance. Required: ${errorDetail.currency} ${errorDetail.required_amount?.toFixed(2)}, Current: ${errorDetail.currency} ${errorDetail.current_balance?.toFixed(2)}. Please add balance first.`);
        } else {
          setFormError(errorDetail.message || 'Failed to create staff');
        }
      } else {
        setFormError(errorDetail || 'Failed to create staff');
      }
    } finally {
      setStaffFormSaving(false);
    }
  };
  
  const handleEditStaff = async (e) => {
    e.preventDefault();
    if (staffFormSaving) return;
    setFormError('');
    setStaffFormSaving(true);

    try {
      const updateData = {
        name: formData.name,
        department: formData.department,
        shift_start: formData.shift_start,
        shift_end: formData.shift_end,
        timezone: formData.timezone,
        grace_period: formData.grace_period,
        force_break_time: formData.force_break_time,
        allow_screenshot: formData.allow_screenshot,
        screenshot_interval: formData.screenshot_interval,
        working_days: formData.working_days,
        allowed_login_devices: formData.allowed_login_devices,
        status: formData.status,
        staff_type: formData.staff_type,
        team_ids: formData.staff_type === 'staff' ? formData.team_ids : [],
        group_policy_id: formData.group_policy_id || null,  // null = explicitly remove policy
        telegram_alert: formData.telegram_alert
      };
      
      // Only include password if it was entered
      if (formData.password && formData.password.length >= 6) {
        updateData.password = formData.password;
      }
      
      await axios.put(
        `${API}/admin/staff/${selectedStaff.staff_id}`,
        updateData,
        { withCredentials: true }
      );
      
      setEditDialogOpen(false);
      setSelectedStaff(null);
      fetchStaff();
      fetchTeams(); // Refresh teams to update staff counts
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        if (errorDetail.message === 'Insufficient balance') {
          setFormError(`Insufficient balance. Required: ${errorDetail.currency} ${errorDetail.required_amount?.toFixed(2)}, Current: ${errorDetail.currency} ${errorDetail.current_balance?.toFixed(2)}. Please add balance first.`);
        } else {
          setFormError(errorDetail.message || 'Failed to update staff');
        }
      } else {
        setFormError(errorDetail || 'Failed to update staff');
      }
    } finally {
      setStaffFormSaving(false);
    }
  };
  
  const handleDeleteStaff = async () => {
    try {
      await axios.delete(
        `${API}/admin/staff/${selectedStaff.staff_id}`,
        { withCredentials: true }
      );
      
      setDeleteDialogOpen(false);
      setSelectedStaff(null);
      fetchStaff();
    } catch (error) {
      console.error('Failed to delete staff:', error);
    }
  };
  
  const openEditDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setFormError('');
    setFormData({
      name: staffMember.name,
      email: staffMember.email,
      password: '',
      department: staffMember.department || '',
      shift_start: staffMember.shift_start || '09:00',
      shift_end: staffMember.shift_end || '17:00',
      timezone: staffMember.timezone || 'UTC',
      grace_period: staffMember.grace_period || 15,
      force_break_time: staffMember.force_break_time || 0,
      allow_screenshot: staffMember.allow_screenshot || false,
      screenshot_interval: staffMember.screenshot_interval || 5,
      working_days: staffMember.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      allowed_login_devices: staffMember.allowed_login_devices || ['desktop', 'mobile', 'web'],
      status: staffMember.status || 'active',
      staff_type: staffMember.staff_type || 'staff',
      team_ids: staffMember.team_ids || [],
      group_policy_id: staffMember.group_policy_id || '',
      telegram_alert: staffMember.telegram_alert || false
    });
    setEditDialogOpen(true);
  };
  
  const openDeleteDialog = (staffMember) => {
    setSelectedStaff(staffMember);
    setDeleteDialogOpen(true);
  };
  
  // State for tabs
  const [activeTab, setActiveTab] = useState('active');
  
  // Filter staff by search term
  const searchFilteredStaff = staff.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.department && s.department.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  // Separate active and inactive staff
  const activeStaff = searchFilteredStaff.filter((s) => s.status === 'active');
  const inactiveStaff = searchFilteredStaff.filter((s) => s.status === 'inactive' || s.status === 'fired');

  // Helper to format timezone for display
  const formatTimezone = (tz) => {
    const found = TIMEZONES.find(t => t.value === tz);
    return found ? found.value : tz || 'UTC';
  };

  const totalActive = staff.filter((s) => s.status === 'active').length;
  const totalInactive = staff.filter((s) => s.status === 'inactive' || s.status === 'fired').length;
  const totalManagers = staff.filter((s) => s.staff_type === 'manager').length;
  const withScreenshots = staff.filter((s) => s.allow_screenshot).length;

  const staffKpis = [
    { title: 'Active', value: totalActive, icon: Users, accent: 'from-indigo-500 to-purple-500', soft: 'bg-indigo-50 text-indigo-800' },
    { title: 'Inactive', value: totalInactive, icon: UserCog, accent: 'from-slate-400 to-slate-500', soft: 'bg-slate-100 text-slate-700' },
    { title: 'Managers', value: totalManagers, icon: Shield, accent: 'from-purple-500 to-pink-500', soft: 'bg-purple-50 text-purple-700' },
    { title: 'Screenshots', value: withScreenshots, icon: Camera, accent: 'from-amber-500 to-orange-500', soft: 'bg-amber-50 text-amber-800' },
  ];

  const statusPill = (status) => {
    if (status === 'active') return 'bg-indigo-50 text-indigo-700 ring-indigo-200';
    if (status === 'fired') return 'bg-rose-50 text-rose-700 ring-rose-200';
    return 'bg-slate-100 text-slate-600 ring-slate-200';
  };

  const renderStaffCard = (staffMember, inactive = false) => {
    const initial = (staffMember.name || '?').charAt(0).toUpperCase();
    return (
      <div
        key={staffMember.staff_id}
        data-testid={`staff-row-${staffMember.staff_id}`}
        className={`att-fade-up group relative flex flex-col overflow-hidden rounded-[1.75rem] border bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.45)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-24px_rgba(15,23,42,0.35)] ${
          inactive
            ? 'border-slate-200/90 opacity-95'
            : staffMember.staff_type === 'manager'
              ? 'border-purple-200/80'
              : 'border-indigo-200/70'
        }`}
      >
        <div
          className={`h-1.5 w-full ${
            inactive
              ? 'bg-gradient-to-r from-slate-300 to-slate-400'
              : staffMember.staff_type === 'manager'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500'
                : 'bg-gradient-to-r from-indigo-500 to-purple-500'
          }`}
        />
        <div className="flex flex-1 flex-col gap-4 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white shadow-md ${
                  inactive
                    ? 'bg-gradient-to-br from-slate-400 to-slate-500'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                }`}
              >
                {initial}
                {!inactive && (
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-indigo-400 att-live-dot" />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold tracking-tight text-slate-900">
                  {staffMember.name}
                </p>
                <p className="truncate text-sm text-slate-500">{staffMember.email}</p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1.5">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusPill(staffMember.status)}`}>
                {staffMember.status}
              </span>
              {staffMember.staff_type === 'manager' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-semibold text-purple-700 ring-1 ring-purple-200">
                  <Shield className="h-3 w-3" /> Manager
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Department</p>
              <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">
                {staffMember.department || '—'}
              </p>
            </div>
            <div className="rounded-2xl bg-slate-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Timezone</p>
              <p className="mt-0.5 flex items-center gap-1 truncate text-sm font-semibold text-slate-800">
                <Globe className="h-3.5 w-3.5 shrink-0 text-purple-600" />
                <span className="truncate">{formatTimezone(staffMember.timezone)}</span>
              </p>
            </div>
            <div className="rounded-2xl bg-indigo-50/70 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600/80">Shift</p>
              <p className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-slate-800">
                <Clock className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
                {staffMember.shift_start && staffMember.shift_end
                  ? `${staffMember.shift_start} – ${staffMember.shift_end}`
                  : '—'}
              </p>
            </div>
            <div className="rounded-2xl bg-amber-50/60 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">Screenshot</p>
              <p className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-slate-800">
                {staffMember.allow_screenshot ? (
                  <>
                    <Camera className="h-3.5 w-3.5 text-amber-600" />
                    {staffMember.screenshot_interval || 5}min
                  </>
                ) : (
                  <span className="text-slate-400">Off</span>
                )}
              </p>
            </div>
          </div>

          <div className={`mt-auto grid gap-1.5 border-t border-slate-100 pt-3 ${inactive ? 'grid-cols-4' : 'grid-cols-5'}`}>
            {!inactive && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/admin/staff/gps/${staffMember.staff_id}`)}
                className={`h-10 w-full rounded-xl ${staffMember.has_gps_config ? 'text-indigo-600 hover:bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
                title="GPS Coordinates"
                data-testid={`gps-staff-${staffMember.staff_id}`}
              >
                <MapPin className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openPersonalDataDialog(staffMember)}
              className={`h-10 w-full rounded-xl ${staffMember.has_personal_data ? 'text-indigo-600 hover:bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
              title="Personal Data"
            >
              <UserCircle className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openSalaryDialog(staffMember)}
              className={`h-10 w-full rounded-xl ${staffMember.has_salary_setup ? 'text-indigo-600 hover:bg-indigo-50' : 'text-slate-400 hover:bg-slate-50'}`}
              title="Salary Setup"
            >
              <DollarSign className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEditDialog(staffMember)}
              className="h-10 w-full rounded-xl text-slate-600 hover:bg-slate-50"
              data-testid={`edit-staff-${staffMember.staff_id}`}
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openDeleteDialog(staffMember)}
              className="h-10 w-full rounded-xl text-rose-600 hover:bg-rose-50"
              data-testid={`delete-staff-${staffMember.staff_id}`}
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }
  
  return (
    <div className="relative pb-8" data-testid="staff-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        {/* Hero */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{t('staff.title')}</h2>
                <p className="mt-1.5 text-sm text-indigo-50/90">
                  Manage accounts, shifts, and monitoring for your team
                </p>
              </div>
              <Dialog open={createDialogOpen} onOpenChange={(open) => {
                if (staffFormSaving) return;
                if (open && companyInfo) {
                  setFormError('');
                  setFormData(prev => ({
                    ...prev,
                    timezone: companyInfo.timezone || 'UTC',
                    working_days: companyInfo.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
                  }));
                }
                if (!open) setFormError('');
                setCreateDialogOpen(open);
              }}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="add-staff-button"
                    className="h-11 rounded-2xl bg-white px-5 font-semibold text-indigo-700 shadow-md hover:bg-indigo-50"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Staff
                  </Button>
                </DialogTrigger>
                <DialogContent
                  className="flex max-h-[90vh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:w-full"
                  onInteractOutside={(e) => { if (staffFormSaving) e.preventDefault(); }}
                  onEscapeKeyDown={(e) => { if (staffFormSaving) e.preventDefault(); }}
                >
                  <StaffFormWizard
                    open={createDialogOpen}
                    mode="create"
                    formData={formData}
                    setFormData={setFormData}
                    formError={formError}
                    onSubmit={handleCreateStaff}
                    saving={staffFormSaving}
                    teams={teams}
                    usagePolicies={usagePolicies}
                    telegramEnabled={telegramEnabled}
                    TimezoneField={TimezoneCombobox}
                    companyTimezone={companyInfo?.timezone}
                    t={t}
                    submitTestId="create-staff-submit"
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <p className="text-sm font-medium text-slate-600">
              {staff.length} total · {totalActive} active
            </p>
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
              <Globe className="h-3 w-3 text-purple-600" /> Staff timezones
            </span>
          </div>
        </section>

        {/* KPIs */}
        <section className="att-fade-up grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4" style={{ animationDelay: '60ms' }}>
          {staffKpis.map((k) => (
            <div
              key={k.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl transition group-hover:opacity-20`} />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.title}</p>
                  <p className="mt-2 text-3xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-4xl">{k.value}</p>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}>
                  <k.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Roster */}
        <section className="att-fade-up space-y-4" style={{ animationDelay: '120ms' }}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-xl font-bold tracking-tight text-slate-900">
                  {activeTab === 'active' ? 'Active roster' : 'Inactive roster'}
                </h3>
                <p className="text-sm text-slate-500">
                  {activeTab === 'active'
                    ? `${activeStaff.length} staff currently active`
                    : `${inactiveStaff.length} inactive or terminated`}
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <TabsList className="h-11 rounded-2xl bg-slate-100/90 p-1">
                  <TabsTrigger
                    value="active"
                    className="rounded-xl px-4 data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-sm"
                  >
                    Active
                    <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                      {totalActive}
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="inactive"
                    className="rounded-xl px-4 data-[state=active]:bg-white data-[state=active]:text-slate-700 data-[state=active]:shadow-sm"
                  >
                    Inactive
                    <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
                      {totalInactive}
                    </span>
                  </TabsTrigger>
                </TabsList>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search staff…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="h-11 rounded-2xl border-slate-200 bg-white/90 pl-10 shadow-sm backdrop-blur"
                    data-testid="search-staff-input"
                  />
                </div>
              </div>
            </div>

            <TabsContent value="active" className="mt-0 focus-visible:outline-none">
              {activeStaff.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-20 text-center text-slate-500">
                  {searchTerm ? `No staff matching "${searchTerm}"` : 'No active staff found'}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {activeStaff.map((s) => renderStaffCard(s, false))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="inactive" className="mt-0 focus-visible:outline-none">
              {inactiveStaff.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-20 text-center text-slate-500">
                  {searchTerm ? `No staff matching "${searchTerm}"` : 'No inactive or fired staff found'}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {inactiveStaff.map((s) => renderStaffCard(s, true))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </section>
      </div>
      
      {/* Salary Setup Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:w-full">
          <SalarySetupWizard
            open={salaryDialogOpen}
            staffName={salaryStaff?.name}
            loading={salaryLoading}
            salaryData={salaryData}
            setSalaryData={setSalaryData}
            currencies={CURRENCIES}
            getCurrencyInfo={getCurrencyInfo}
            onSave={handleSaveSalary}
            onDelete={handleDeleteSalary}
            onCancel={() => setSalaryDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        if (staffFormSaving) return;
        if (!open) setFormError('');
        setEditDialogOpen(open);
      }}>
        <DialogContent
          className="flex max-h-[90vh] w-[calc(100%-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-3xl border-0 p-0 shadow-2xl sm:w-full"
          onInteractOutside={(e) => { if (staffFormSaving) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (staffFormSaving) e.preventDefault(); }}
        >
          <StaffFormWizard
            open={editDialogOpen}
            mode="edit"
            formData={formData}
            setFormData={setFormData}
            formError={formError}
            onSubmit={handleEditStaff}
            saving={staffFormSaving}
            teams={teams}
            usagePolicies={usagePolicies}
            telegramEnabled={telegramEnabled}
            TimezoneField={TimezoneCombobox}
            companyTimezone={companyInfo?.timezone}
            t={t}
            submitTestId="update-staff-submit"
          />
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedStaff?.name} and all their attendance records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl" data-testid="delete-staff-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStaff}
              className="rounded-2xl bg-rose-600 hover:bg-rose-700"
              data-testid="delete-staff-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Personal Data Dialog */}
      <Dialog open={personalDataDialogOpen} onOpenChange={setPersonalDataDialogOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="h-5 w-5 text-indigo-600" />
              Personal Data - {personalDataStaff?.name}
            </DialogTitle>
            <DialogDescription>
              Manage documents and next of kin information
            </DialogDescription>
          </DialogHeader>
          
          {personalDataLoading ? (
            <div className="flex justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            </div>
          ) : (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6">
                {/* Documents Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="flex items-center gap-2 text-lg font-semibold">
                      <FileText className="h-5 w-5 text-purple-600" />
                      Documents
                    </h3>
                    <div>
                      <input
                        type="file"
                        id="document-upload"
                        className="hidden"
                        onChange={handleUploadDocument}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-xl"
                        onClick={() => document.getElementById('document-upload').click()}
                        disabled={uploadingDoc}
                        data-testid="upload-document-btn"
                      >
                        {uploadingDoc ? (
                          <>
                            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="mr-2 h-4 w-4" />
                            Upload Document
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {documents.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 py-6 text-center">
                      <FileText className="mx-auto mb-2 h-8 w-8 text-slate-400" />
                      <p className="text-sm text-slate-500">No documents uploaded yet</p>
                      <p className="text-xs text-slate-400">PDF, DOC, DOCX, JPG, PNG accepted (max 5MB)</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div
                          key={doc.document_id}
                          className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-purple-600" />
                            <div>
                              <p className="font-medium text-sm">{doc.filename}</p>
                              <p className="text-xs text-gray-500">
                                {(doc.size / 1024).toFixed(1)} KB • {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadDocument(doc)}
                              title="Download"
                            >
                              <Download className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteDocument(doc.document_id)}
                              title="Delete"
                            >
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Next of Kin Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-indigo-600" />
                      Next of Kin
                    </h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddNextOfKin}
                      data-testid="add-next-of-kin-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Next of Kin
                    </Button>
                  </div>
                  
                  {nextOfKin.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed">
                      <UserCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-500 text-sm">No next of kin added yet</p>
                      <p className="text-xs text-gray-400">Click &quot;Add Next of Kin&quot; to add emergency contacts</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {nextOfKin.map((kin, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg border space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-600">Contact #{index + 1}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveNextOfKin(index)}
                              className="h-8 w-8"
                            >
                              <X className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">{t("common.name")}</Label>
                              <Input
                                placeholder="Full Name"
                                value={kin.name}
                                onChange={(e) => handleNextOfKinChange(index, 'name', e.target.value)}
                                data-testid={`nok-name-${index}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Relation</Label>
                              <Input
                                placeholder="e.g. Spouse, Parent"
                                value={kin.relation}
                                onChange={(e) => handleNextOfKinChange(index, 'relation', e.target.value)}
                                data-testid={`nok-relation-${index}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Contact Number</Label>
                              <Input
                                placeholder="+1234567890"
                                value={kin.contact_number}
                                onChange={(e) => handleNextOfKinChange(index, 'contact_number', e.target.value)}
                                data-testid={`nok-contact-${index}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setPersonalDataDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePersonalData} data-testid="save-personal-data-btn">
              Save Personal Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StaffPage;
